/**
 * Master Library Taxonomy Page
 * CRM-style management of document references and publishing workflow
 * 
 * Shows: Document Reference | Name | Status | Regulator | Theme | Type | Created | Synced | Actions
 * Allows: Filtering, Searching, Publishing drafts, Viewing reference history
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Table, FileText, MagnifyingGlass, Funnel, CaretUp, CaretDown,
  CheckCircle, Clock, ArrowsClockwise, Eye, PencilSimple,
  CloudArrowUp, Tag, Buildings, Scales, Shield, Hash, Calendar,
  Export, CaretLeft, CaretRight, Info, Warning, Check
} from '@phosphor-icons/react';
import { API, useAuth } from '@/App';
import Sidebar from '@/components/Sidebar';
import PageBreadcrumb from '@/components/PageBreadcrumb';

// Regulator colors for visual distinction
const REGULATOR_COLORS = {
  FIC: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  FSCA: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  SARB: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  PA: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  CIPC: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  INFO: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
  NCR: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  JSE: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
  IRBA: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
  NT: 'bg-rose-500/20 text-rose-400 border-rose-500/30',
  OTH: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

// Regulator list for taxonomy
const REGULATORS = [
  { id: 'FIC', label: 'Financial Intelligence Centre' },
  { id: 'FSCA', label: 'Financial Sector Conduct Authority' },
  { id: 'SARB', label: 'South African Reserve Bank' },
  { id: 'PA', label: 'Prudential Authority' },
  { id: 'CIPC', label: 'Companies and Intellectual Property Commission' },
  { id: 'INFO', label: 'Information Regulator' },
  { id: 'NCR', label: 'National Credit Regulator' },
  { id: 'JSE', label: 'Johannesburg Stock Exchange' },
  { id: 'IRBA', label: 'Independent Regulatory Board for Auditors' },
  { id: 'NT', label: 'National Treasury' },
  { id: 'OTH', label: 'Other' }
];

// Document types for taxonomy
const DOC_TYPES = [
  { id: 'act', label: 'Act' },
  { id: 'regulation', label: 'Regulation' },
  { id: 'directive', label: 'Directive' },
  { id: 'guidance', label: 'Guidance Note' },
  { id: 'circular', label: 'Circular' },
  { id: 'notice', label: 'Notice' },
  { id: 'standard', label: 'Standard' },
  { id: 'policy', label: 'Policy' },
  { id: 'framework', label: 'Framework' }
];

const STATUS_STYLES = {
  published: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  draft: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
};

const MasterLibraryTaxonomyPage = () => {
  const { token } = useAuth();
  
  // State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    regulator: '',
    theme: '',
    doc_type: '',
    synced: '',
    search: ''
  });
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25;
  
  // Sorting
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Modals
  const [publishModal, setPublishModal] = useState(null);
  const [detailModal, setDetailModal] = useState(null);

  // Fetch taxonomy (using aura-library endpoint)
  const fetchTaxonomy = useCallback(async () => {
    try {
      // Use the custom-themes endpoint for taxonomy info
      const response = await axios.get(`${API}/admin/aura-library/custom-themes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Transform to taxonomy format
      const themes = response.data.themes || [];
      setTaxonomy({
        themes: themes.map(t => ({ code: t.id, label: t.label })),
        regulators: REGULATORS,
        doc_types: DOC_TYPES
      });
    } catch (error) {
      console.error('Failed to fetch taxonomy:', error);
    }
  }, [token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/aura-library/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  // Fetch documents
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/aura-library/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let docs = response.data.documents || [];
      
      // Apply client-side filtering
      if (filters.status) {
        docs = docs.filter(d => filters.status === 'draft' ? d.is_draft : !d.is_draft);
      }
      if (filters.regulator) {
        docs = docs.filter(d => d.regulator === filters.regulator);
      }
      if (filters.theme) {
        docs = docs.filter(d => d.theme === filters.theme);
      }
      if (filters.doc_type) {
        docs = docs.filter(d => d.document_type === filters.doc_type);
      }
      if (filters.synced !== '') {
        docs = docs.filter(d => filters.synced === 'true' ? d.synced_to_tenants : !d.synced_to_tenants);
      }
      if (filters.search) {
        const search = filters.search.toLowerCase();
        docs = docs.filter(d => 
          (d.name || '').toLowerCase().includes(search) ||
          (d.reference_number || '').toLowerCase().includes(search)
        );
      }
      
      // Apply sorting
      docs.sort((a, b) => {
        const aVal = a[sortBy] || '';
        const bVal = b[sortBy] || '';
        return sortOrder === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      });
      
      // Apply pagination
      const startIdx = (page - 1) * limit;
      const paginatedDocs = docs.slice(startIdx, startIdx + limit);
      
      setDocuments(paginatedDocs);
      setTotal(docs.length);
      setTotalPages(Math.ceil(docs.length / limit));
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token, page, limit, sortBy, sortOrder, filters]);

  // Initial load
  useEffect(() => {
    fetchTaxonomy();
    fetchStats();
  }, [fetchTaxonomy, fetchStats]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handle publish (sync to tenant libraries)
  const handlePublish = async () => {
    if (!publishModal) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/aura-library/sync-to-tenants/${publishModal.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Document synced to tenant libraries`);
        setPublishModal(null);
        fetchDocuments();
        fetchStats();
      } else {
        toast.error(response.data.error || 'Failed to sync');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync document');
    }
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      regulator: '',
      theme: '',
      doc_type: '',
      synced: '',
      search: ''
    });
    setPage(1);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <div className="flex-1 ml-64 p-6" data-testid="taxonomy-page">
        {/* Breadcrumb */}
        <div className="mb-4">
          <PageBreadcrumb items={[
            { label: 'Admin', href: '/dashboard' },
            { label: 'Master Library Taxonomy' }
          ]} />
        </div>
        
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-gold/20 rounded-lg">
              <Tag size={24} className="text-gold" weight="fill" />
            </div>
            <div>
              <h1 className="text-2xl font-heading text-text-primary">Master Library Taxonomy</h1>
              <p className="text-sm text-text-secondary">Document reference registry and publishing workflow</p>
            </div>
          </div>
        </div>

      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <StatCard label="Total Documents" value={stats.total_documents} icon={<FileText />} />
          <StatCard label="Published" value={stats.published_count} icon={<CheckCircle />} color="emerald" />
          <StatCard label="Drafts" value={stats.draft_count} icon={<Clock />} color="amber" />
          <StatCard label="Synced" value={stats.synced_count} icon={<CloudArrowUp />} color="blue" />
          <StatCard label="Not Synced" value={stats.not_synced_count} icon={<Warning />} color="gray" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-surface border border-border rounded-lg p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Funnel size={18} className="text-text-secondary" />
          <span className="text-sm font-medium text-text-primary">Filters</span>
          {Object.values(filters).some(v => v !== '') && (
            <button
              onClick={resetFilters}
              className="ml-auto text-xs text-gold hover:text-gold/80"
            >
              Clear all
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {/* Search */}
          <div className="col-span-2">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search reference or name..."
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="w-full pl-9 pr-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
          </div>
          
          {/* Status */}
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
          >
            <option value="">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
          
          {/* Regulator */}
          <select
            value={filters.regulator}
            onChange={(e) => setFilters(f => ({ ...f, regulator: e.target.value }))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
          >
            <option value="">All Regulators</option>
            {taxonomy?.regulators?.map(r => (
              <option key={r.code} value={r.code}>{r.code} - {r.name}</option>
            ))}
          </select>
          
          {/* Theme */}
          <select
            value={filters.theme}
            onChange={(e) => setFilters(f => ({ ...f, theme: e.target.value }))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
          >
            <option value="">All Themes</option>
            {taxonomy?.themes?.map(t => (
              <option key={t.code} value={t.code}>{t.code} - {t.name}</option>
            ))}
          </select>
          
          {/* Synced */}
          <select
            value={filters.synced}
            onChange={(e) => setFilters(f => ({ ...f, synced: e.target.value }))}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-primary focus:border-gold focus:outline-none"
          >
            <option value="">All Sync Status</option>
            <option value="true">Synced</option>
            <option value="false">Not Synced</option>
          </select>
        </div>
      </div>

      {/* Documents Table */}
      <div className="bg-surface border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-background-surface border-b border-border">
              <tr>
                <SortHeader label="Reference" field="document_ref" current={sortBy} order={sortOrder} onSort={handleSort} />
                <SortHeader label="Name" field="name" current={sortBy} order={sortOrder} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">Regulator</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">Theme</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">Type</th>
                <SortHeader label="Created" field="created_at" current={sortBy} order={sortOrder} onSort={handleSort} />
                <th className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase">Synced</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-text-secondary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-text-secondary">
                    <ArrowsClockwise size={24} className="animate-spin mx-auto mb-2" />
                    Loading documents...
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-text-secondary">
                    No documents found
                  </td>
                </tr>
              ) : (
                documents.map((doc) => (
                  <DocumentRow
                    key={doc.id}
                    doc={doc}
                    taxonomy={taxonomy}
                    onPublish={() => {
                      setPublishModal(doc);
                      handlePublish(false);
                    }}
                    onView={() => setDetailModal(doc)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="px-4 py-3 border-t border-border flex items-center justify-between">
          <span className="text-sm text-text-secondary">
            Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, total)} of {total} documents
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50"
            >
              <CaretLeft size={16} />
            </button>
            <span className="text-sm text-text-primary">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-border hover:bg-background disabled:opacity-50"
            >
              <CaretRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Publish Modal */}
      <AnimatePresence>
        {publishModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setPublishModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface border border-border rounded-xl p-6 max-w-md w-full"
            >
              <h3 className="text-lg font-semibold text-text-primary mb-4">Publish Document</h3>
              
              {publishModal.preview ? (
                <div className="space-y-4">
                  <div className="bg-background rounded-lg p-4">
                    <p className="text-sm text-text-secondary mb-2">Document</p>
                    <p className="font-medium text-text-primary">{publishModal.preview.document_name}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                      <p className="text-xs text-amber-400 mb-1">Current (Draft)</p>
                      <p className="font-mono text-sm text-amber-300">{publishModal.preview.current_reference}</p>
                    </div>
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                      <p className="text-xs text-emerald-400 mb-1">New (Published)</p>
                      <p className="font-mono text-sm text-emerald-300">{publishModal.preview.new_reference}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <Info size={18} className="text-blue-400" />
                    <p className="text-sm text-blue-300">The status character will change from D to P</p>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setPublishModal(null)}
                      className="flex-1 px-4 py-2 border border-border rounded-lg text-text-secondary hover:bg-background"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handlePublish(true)}
                      className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 flex items-center justify-center gap-2"
                    >
                      <Check size={18} />
                      Confirm Publish
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-8">
                  <ArrowsClockwise size={24} className="animate-spin text-text-secondary" />
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {detailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => setDetailModal(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-surface border border-border rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <h3 className="text-lg font-semibold text-text-primary mb-4">Document Details</h3>
              
              <div className="space-y-4">
                <div className="bg-background rounded-lg p-4">
                  <p className="text-xs text-text-tertiary mb-1">Document Reference</p>
                  <p className="font-mono text-lg text-gold">{detailModal.reference_number || detailModal.document_ref || 'Not assigned'}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Name</p>
                    <p className="text-text-primary">{detailModal.name || detailModal.document_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Status</p>
                    <span className={`inline-flex px-2 py-1 rounded text-xs ${
                      detailModal.is_draft ? STATUS_STYLES.draft : STATUS_STYLES.published
                    }`}>
                      {detailModal.is_draft ? 'Draft' : 'Published'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Regulator</p>
                    <p className="text-text-primary">{detailModal.regulator || detailModal.regulator_code || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Theme</p>
                    <p className="text-text-primary">{detailModal.theme || detailModal.theme_code || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Document Type</p>
                    <p className="text-text-primary">{detailModal.document_type || detailModal.document_type_code || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-tertiary mb-1">Created</p>
                    <p className="text-text-primary">{detailModal.created_at ? new Date(detailModal.created_at).toLocaleDateString() : '—'}</p>
                  </div>
                </div>
                
                {detailModal.reference_history?.length > 0 && (
                  <div>
                    <p className="text-xs text-text-tertiary mb-2">Reference History</p>
                    <div className="space-y-2">
                      {detailModal.reference_history.map((h, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <span className="font-mono text-text-secondary">{h.ref}</span>
                          <span className="text-text-tertiary">→</span>
                          <span className="text-text-tertiary">{new Date(h.to).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={() => setDetailModal(null)}
                className="mt-6 w-full px-4 py-2 border border-border rounded-lg text-text-secondary hover:bg-background"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ label, value, icon, color = 'gold' }) => {
  const colors = {
    gold: 'bg-gold/20 text-gold',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    amber: 'bg-amber-500/20 text-amber-400',
    blue: 'bg-blue-500/20 text-blue-400',
    gray: 'bg-gray-500/20 text-gray-400',
  };
  
  return (
    <div className="bg-surface border border-border rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          {React.cloneElement(icon, { size: 20 })}
        </div>
        <div>
          <p className="text-2xl font-semibold text-text-primary">{value}</p>
          <p className="text-xs text-text-secondary">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Sort Header Component
const SortHeader = ({ label, field, current, order, onSort }) => (
  <th
    className="px-4 py-3 text-left text-xs font-medium text-text-secondary uppercase cursor-pointer hover:text-text-primary"
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      {label}
      {current === field && (
        order === 'asc' ? <CaretUp size={12} /> : <CaretDown size={12} />
      )}
    </div>
  </th>
);

// Document Row Component
const DocumentRow = ({ doc, taxonomy, onPublish, onView }) => {
  const isDraft = doc.is_draft || doc.status_code === 'D';
  const isSynced = doc.synced_to_tenants;
  
  // Map actual database fields to expected display fields
  const reference = doc.reference_number || doc.document_ref || '—';
  const regulator = doc.regulator || doc.regulator_code || '—';
  const theme = doc.theme || doc.theme_code || '—';
  const docType = doc.document_type || doc.document_type_code || '—';
  
  return (
    <tr className="hover:bg-background/50 transition-colors">
      <td className="px-4 py-3">
        <span className="font-mono text-sm text-gold">{reference}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-text-primary line-clamp-1">{doc.name || doc.document_name}</span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex px-2 py-1 rounded text-xs border ${
          isDraft ? STATUS_STYLES.draft : STATUS_STYLES.published
        }`}>
          {isDraft ? 'Draft' : 'Published'}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className={`inline-flex px-2 py-1 rounded text-xs border ${
          REGULATOR_COLORS[regulator] || REGULATOR_COLORS.OTH
        }`}>
          {regulator}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-text-secondary">{theme}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-text-secondary">{docType}</span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-text-secondary">
          {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '—'}
        </span>
      </td>
      <td className="px-4 py-3">
        {isSynced ? (
          <CheckCircle size={18} className="text-emerald-400" weight="fill" />
        ) : (
          <span className="text-text-tertiary">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={onView}
            className="p-1.5 rounded hover:bg-background text-text-secondary hover:text-text-primary"
            title="View details"
          >
            <Eye size={16} />
          </button>
          {isDraft && (
            <button
              onClick={onPublish}
              className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-xs"
              title="Publish"
            >
              Publish
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default MasterLibraryTaxonomyPage;
